import type * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FirebaseError } from 'firebase/app';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Checkbox from '@cloudscape-design/components/checkbox';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import Select, { type SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import {
  formatCurrency,
  PaymentStatus,
  priceCentsToUnits,
  RefundReason,
  type Currency,
  type Payment,
  type RefundReason as RefundReasonT,
} from '@etiquetachef/types';
import { toast } from '@/lib/notifications';
import { cancelSubscriptionAdminCallable, requestRefundCallable } from '@/lib/callables';
import { gatewayErrorMessage } from '../billing/gatewayErrors';
import { useTenants } from '@/features/tenants/useTenants';
import {
  subscribeTenantPayments,
  type PaymentListResult,
} from '@/lib/firestoreTenantDetail';

const TAG = 'admin/request-refund';

/** Filtros estáveis (referência fixa) para o hook de tenants. */
const TENANT_FILTERS = { status: null } as const;

/**
 * Motivos oferecidos no dialog, na ordem pedida pelo owner
 * (comercial → suporte → duplicata → fraude → outro → MBG → disputa).
 * Super-admin pode escolher qualquer um — inclusive `MBG`/`DISPUTE`, antes
 * só gerados pelo sistema.
 */
const REASON_OPTIONS: RefundReasonT[] = [
  RefundReason.COMMERCIAL,
  RefundReason.REQUESTED_BY_SUPPORT,
  RefundReason.DUPLICATE,
  RefundReason.FRAUDULENT,
  RefundReason.OTHER,
  RefundReason.MBG,
  RefundReason.DISPUTE,
];

/** Pagamentos que ainda têm saldo reembolsável. */
function isRefundable(p: Payment): boolean {
  const stillRefundable = p.refunded_amount < p.amount;
  return (
    stillRefundable &&
    (p.status === PaymentStatus.CONFIRMED ||
      p.status === PaymentStatus.PARTIALLY_REFUNDED)
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
  /**
   * Sprint 5 PR F — quando aberto a partir de uma transação, o caller passa
   * `tenantId`/`paymentId` para preencher e travar os campos. Quando aberto
   * pelo botão genérico (RefundsPage), super-admin busca o tenant pelo nome
   * e escolhe o pagamento na lista.
   */
  tenantId?: string;
  paymentId?: string;
};

/**
 * Modal de reembolso manual (super-admin): escolhe tenant e pagamento reembolsável, valor
 * (parcial ou total), motivo e notas; opcionalmente cancela a assinatura junto.
 *
 * Origem: sem issue — commit 973be8d9c, 2026-05-05 12:08 +0200 (Sprint 5 PR H); a callable
 * `requestRefund` é do PR F (a69750e1f, 2026-05-05 12:55 +0200).
 *
 * Por quê: o refund MBG automático (`requestTenantDeletion`) e o de disputa são do sistema;
 * este é o caminho humano. UX: tenant por nome num `Select` com busca; ao escolher, carrega os
 * pagamentos com saldo reembolsável (`isRefundable`: CONFIRMED/PARTIALLY_REFUNDED e
 * `refunded_amount < amount`) e o pagamento escolhido preenche valor total e moeda.
 *
 * Contrato: `tenantId`/`paymentId` opcionais travam os campos (aberto a partir de uma
 * transação). Valor digitado em unidades ("19.90") → CENTAVOS no submit; vazio = total.
 * Inválido se ≤ 0 ou maior que o saldo. Chama `requestRefund` (Functions) e, se `alsoCancel`,
 * `cancelSubscriptionAdmin` com `reason: refund_<motivo>` — o cancelamento só roda se o
 * reembolso deu certo, e a falha dele vira toast com o `code`, SEM desfazer o reembolso (não
 * há transação entre os dois). Toda escrita é no servidor; aqui só leitura via
 * `subscribeTenantPayments`.
 *
 * Usado por: `routes/RefundsPage.tsx` (web `apps/admin`, botão genérico);
 * `RequestRefundDialog.test.tsx` cobre validação e travas.
 *
 * Dados: `tenants/{tenantId}/payments/{paymentId}` (`PaymentSchema`,
 * `amount`/`refunded_amount` em CENTAVOS, como o gateway envia); entrada da callable
 * `RequestRefundInput` e motivos `RefundReason` — `packages/types/src/schemas.ts`.
 *
 * Dicas: `REASON_OPTIONS` está na ordem pedida pelo owner (comercial → suporte → duplicata →
 * fraude → outro → MBG → disputa) — super-admin pode escolher qualquer um, inclusive os que
 * antes só o sistema gerava. Não reordenar "por alfabética".
 *
 * Histórico:
 *  - 2026-06-08 22:57 +0200 — (39-F) migração para Cloudscape.
 *  - 2026-06-09 21:47 +0200 — (sem issue, a5a6b6812) tenant por nome + lista de pagamentos.
 *  - 2026-06-09 22:28 +0200 — (sem issue, 6511c58ea) checkbox "cancelar a assinatura também".
 *  - 2026-10-07 13:05 +0200 — (#795, 1º nível item 2) `requestId` por pedido:
 *    retry não estorna em dobro.
 *  - 2026-10-13 18:05 +0200 — (#961) "cancelar também" manda `immediate: true`:
 *    corta já, no gateway e aqui (o cancelamento comum do admin vale no fim do
 *    período pago).
 *  - 2026-10-25 15:00 +0100 — (#1021) a recusa do `requestRefund` vira `Alert`
 *    dentro do modal: o toast ficava atrás do overlay e o diálogo não fecha no
 *    erro (mesmo achado do CRM, #174).
 */
export function RequestRefundDialog({
  open,
  onClose,
  tenantId: tenantIdProp,
  paymentId: paymentIdProp,
}: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [tenantId, setTenantId] = useState(tenantIdProp ?? '');
  const [paymentId, setPaymentId] = useState(paymentIdProp ?? '');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('');
  const [reason, setReason] = useState<RefundReasonT>(RefundReason.COMMERCIAL);
  const [notes, setNotes] = useState('');
  // Quando marcado, além do reembolso, cancela a assinatura (cliente perde
  // acesso). Default ligado ao selecionar um pagamento (reembolso total).
  const [alsoCancel, setAlsoCancel] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // #1021 (2026-10-25 15:00 +0100): erro do pedido, mostrado dentro do modal.
  const [error, setError] = useState<string | null>(null);
  const [payments, setPayments] = useState<PaymentListResult>({
    kind: 'READY',
    payments: [],
  });

  const tenantsState = useTenants(TENANT_FILTERS);

  // #795 (1º nível item 2, 2026-10-07 13:05 +0200): id do pedido de estorno.
  // Nasce a cada (re)abertura e a cada troca de tenant/pagamento/valor/motivo — mudar
  // o que se pede é pedido novo —, e é reusado no retry do MESMO pedido: o
  // servidor deriva dele o doc `refunds/*` e a chave de idempotência do
  // gateway, então repetir depois de timeout não estorna duas vezes. A trava
  // síncrona fecha o clique duplo antes do re-render do `submitting`.
  const requestIdRef = useRef('');
  const inFlightRef = useRef(false);
  useEffect(() => {
    if (open) requestIdRef.current = crypto.randomUUID();
    // `reason` também (auditoria #795): a Stripe recusa a MESMA chave com
    // parâmetros diferentes (`idempotency_error`), e o motivo vai no pedido.
  }, [open, tenantId, paymentId, amount, reason]);

  // Reset ao (re)abrir.
  useEffect(() => {
    if (open) {
      setTenantId(tenantIdProp ?? '');
      setPaymentId(paymentIdProp ?? '');
      setAmount('');
      setCurrency('');
      setReason(RefundReason.COMMERCIAL);
      setNotes('');
      setAlsoCancel(false);
      setError(null);
    }
  }, [open, tenantIdProp, paymentIdProp]);

  // Carrega pagamentos do tenant selecionado.
  useEffect(() => {
    if (!open || tenantId.trim() === '') {
      setPayments({ kind: 'READY', payments: [] });
      return;
    }
    setPayments({ kind: 'LOADING' });
    return subscribeTenantPayments(tenantId.trim(), setPayments);
  }, [open, tenantId]);

  const tenantOptions = useMemo<SelectProps.Option[]>(() => {
    if (tenantsState.kind !== 'READY') return [];
    return [...tenantsState.tenants]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((tenant) => ({ value: tenant.id, label: tenant.name, description: tenant.id }));
  }, [tenantsState]);

  const selectedTenantOption = useMemo<SelectProps.Option | null>(() => {
    if (tenantId.trim() === '') return null;
    return (
      tenantOptions.find((o) => o.value === tenantId) ?? {
        value: tenantId,
        label: tenantId,
      }
    );
  }, [tenantId, tenantOptions]);

  const refundablePayments = useMemo<Payment[]>(() => {
    if (payments.kind !== 'READY') return [];
    return payments.payments.filter(isRefundable);
  }, [payments]);

  const paymentOptions = useMemo<SelectProps.Option[]>(() => {
    return refundablePayments.map((p) => {
      // Data + hora (hh:mm) do pagamento.
      const when = new Date(p.paid_at ?? p.created_at).toLocaleString(i18n.language, {
        dateStyle: 'short',
        timeStyle: 'short',
      });
      // `amount` é a fonte única em CENTS (exatamente como o gateway envia);
      // o display converte via priceCentsToUnits/formatCurrency.
      const money = formatCurrency(priceCentsToUnits(p.amount), p.currency, i18n.language);
      return {
        value: p.id,
        label: `${money} · ${when}`,
        description: `${t(`admin.finance.refunds.paymentStatus.${p.status}`, {
          defaultValue: p.status,
        })} · ${p.id}`,
      };
    });
  }, [refundablePayments, i18n.language, t]);

  const selectedPayment = useMemo<Payment | null>(() => {
    if (paymentId.trim() === '') return null;
    return refundablePayments.find((p) => p.id === paymentId) ?? null;
  }, [paymentId, refundablePayments]);

  /** Saldo reembolsável (cents) do pagamento selecionado. */
  const refundableCents = selectedPayment
    ? selectedPayment.amount - selectedPayment.refunded_amount
    : null;

  /** Ao selecionar um pagamento, auto-preenche valor (total reembolsável), moeda e
   * "cancelar junto"; desmarcar limpa os três. */
  function handleSelectPayment(value: string | undefined): void {
    setPaymentId(value ?? '');
    const p = refundablePayments.find((x) => x.id === value);
    if (p) {
      setAmount(((p.amount - p.refunded_amount) / 100).toFixed(2));
      setCurrency(p.currency);
      // Default: reembolso total → também cancela a assinatura.
      setAlsoCancel(true);
    } else {
      setAmount('');
      setCurrency('');
      setAlsoCancel(false);
    }
  }

  const reasonOptions = REASON_OPTIONS.map((opt) => ({
    value: opt,
    label: t(`admin.finance.refunds.reason.${opt}`),
  }));

  // Valor em cents (ou undefined = reembolso total). NaN quando inválido.
  const amountCents =
    amount.trim() === '' ? undefined : Math.round(Number(amount) * 100);
  const amountInvalid =
    amountCents !== undefined &&
    (Number.isNaN(amountCents) ||
      amountCents <= 0 ||
      (refundableCents !== null && amountCents > refundableCents));

  const canSubmit =
    tenantId.trim().length > 0 &&
    paymentId.trim().length > 0 &&
    !amountInvalid &&
    !submitting;

  /** Chama `requestRefund` (mesmo `requestId` nos reenvios) e, se ok, cancela a assinatura. */
  async function handleSubmit(): Promise<void> {
    if (!canSubmit || inFlightRef.current) return;
    inFlightRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const result = await requestRefundCallable({
        tenantId: tenantId.trim(),
        paymentId: paymentId.trim(),
        requestId: requestIdRef.current,
        ...(amountCents !== undefined && amountCents > 0 ? { amount: amountCents } : {}),
        reason,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      console.info(`${TAG}: refund issued status=${result.status}`);
      toast.success(t('admin.finance.refunds.requestSuccess', { status: result.status }));

      // Opcional: cancelar a assinatura junto (cliente perde acesso → paywall).
      // Só roda se o reembolso acima deu certo.
      if (alsoCancel) {
        try {
          // #961 (owner, 2026-10-13): reembolsado não tem período pago a
          // preservar — `immediate` encerra já no gateway e corta o acesso. Sem
          // ele o cancelamento do admin vale só no fim do período.
          await cancelSubscriptionAdminCallable({
            tenantId: tenantId.trim(),
            reason: `refund_${reason}`,
            immediate: true,
          });
          toast.success(t('admin.finance.refunds.alsoCancelSuccess'));
        } catch (cancelErr) {
          console.error(`${TAG}: refund ok but cancel failed`, cancelErr);
          const code = cancelErr instanceof FirebaseError ? cancelErr.code : 'unknown';
          toast.error(t('admin.finance.refunds.alsoCancelError', { code }));
        }
      }
      onClose();
    } catch (err) {
      console.error(`${TAG}: failed`, err);
      const code = err instanceof FirebaseError ? err.code : 'unknown';
      // `details.code` traduzido primeiro (#795, auditoria do item 2): a trava
      // do Asaas precisa dizer ao operador para conferir o painel.
      // Auditoria 2026-10-25 15:00 +0100 (#1021): era `toast.error`, que fica atrás do overlay do
      // Modal (mesmo achado do CRM, #174) — e o diálogo não fecha no erro. Vai para o `Alert` abaixo.
      setError(gatewayErrorMessage(err, t) ?? t('admin.finance.refunds.requestError', { code }));
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  }

  const tenantLocked = Boolean(tenantIdProp);
  const paymentLocked = Boolean(paymentIdProp);

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) onClose();
      }}
      header={t('admin.finance.refunds.requestTitle')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={!canSubmit}
              loading={submitting}
              onClick={(): void => {
                void handleSubmit();
              }}
            >
              {t('admin.finance.refunds.requestConfirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          {t('admin.finance.refunds.requestDescription')}
        </Box>

        <FormField label={t('admin.finance.refunds.tenantSelectLabel')}>
          <Select
            selectedOption={selectedTenantOption}
            options={tenantOptions}
            filteringType="auto"
            disabled={tenantLocked || submitting}
            statusType={tenantsState.kind === 'LOADING' ? 'loading' : 'finished'}
            loadingText={t('admin.finance.refunds.loadingTenants')}
            placeholder={t('admin.finance.refunds.tenantSelectPlaceholder')}
            empty={t('admin.finance.refunds.noTenants')}
            onChange={({ detail }): void => {
              const value = detail.selectedOption.value ?? '';
              setTenantId(value);
              // troca de tenant zera o pagamento e o valor
              setPaymentId('');
              setAmount('');
              setCurrency('');
            }}
          />
        </FormField>

        <FormField
          label={t('admin.finance.refunds.paymentSelectLabel')}
          description={
            tenantId.trim() === ''
              ? t('admin.finance.refunds.pickTenantFirst')
              : undefined
          }
        >
          <Select
            selectedOption={
              paymentLocked
                ? { value: paymentId, label: paymentId }
                : (paymentOptions.find((o) => o.value === paymentId) ?? null)
            }
            options={paymentOptions}
            filteringType="auto"
            disabled={paymentLocked || submitting || tenantId.trim() === ''}
            statusType={payments.kind === 'LOADING' ? 'loading' : 'finished'}
            loadingText={t('admin.finance.refunds.loadingPayments')}
            placeholder={t('admin.finance.refunds.paymentSelectPlaceholder')}
            empty={t('admin.finance.refunds.noRefundablePayments')}
            onChange={({ detail }): void => {
              handleSelectPayment(detail.selectedOption.value);
            }}
          />
        </FormField>

        <FormField
          label={t('admin.finance.refunds.amountLabel')}
          description={
            refundableCents !== null
              ? t('admin.finance.refunds.amountRefundableHint', {
                  amount: formatCurrency(
                    priceCentsToUnits(refundableCents),
                    currency as Currency,
                    i18n.language,
                  ),
                })
              : undefined
          }
          constraintText={currency || undefined}
          errorText={
            amountInvalid ? t('admin.finance.refunds.amountInvalid') : undefined
          }
        >
          <Input
            type="number"
            inputMode="decimal"
            value={amount}
            onChange={({ detail }): void => {
              setAmount(detail.value);
              // 2ª auditoria (2026-10-25 21:32 +0100, #1021): editar o campo tira o erro antigo do Alert.
              setError(null);
            }}
            disabled={submitting || paymentId.trim() === ''}
            placeholder={t('admin.finance.refunds.amountPlaceholder')}
          />
        </FormField>

        <FormField label={t('admin.finance.refunds.reasonLabel')}>
          <Select
            selectedOption={reasonOptions.find((o) => o.value === reason) ?? null}
            options={reasonOptions}
            disabled={submitting}
            onChange={({ detail }): void => {
              setReason(detail.selectedOption.value as RefundReasonT);
              // 2ª auditoria (2026-10-25 21:32 +0100, #1021): editar o campo tira o erro antigo do Alert.
              setError(null);
            }}
          />
        </FormField>

        <FormField label={t('admin.finance.refunds.notesLabel')}>
          <Textarea
            value={notes}
            onChange={({ detail }): void => {
              setNotes(detail.value);
              // 2ª auditoria (2026-10-25 21:32 +0100, #1021): editar o campo tira o erro antigo do Alert.
              setError(null);
            }}
            disabled={submitting}
            rows={3}
            placeholder={t('admin.finance.refunds.notesPlaceholder')}
          />
        </FormField>

        <Checkbox
          checked={alsoCancel}
          disabled={submitting || tenantId.trim() === ''}
          onChange={({ detail }): void => {
            setAlsoCancel(detail.checked);
          }}
          description={t('admin.finance.refunds.alsoCancelHint')}
        >
          {t('admin.finance.refunds.alsoCancelLabel')}
        </Checkbox>
        {/* Auditoria 2026-10-25 15:00 +0100 (#1021): erro dentro do modal, junto do footer (#174). */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
